import type { Causas, Embudo as EmbudoDatos, PasoEmbudo } from '../api';

/**
 * Visualización del embudo de un proceso de negocio.
 *
 * El ancho de cada barra es el porcentaje de ejecuciones que alcanzó ese paso,
 * no una escala arbitraria: la caída se ve como caída. Entre paso y paso se
 * anota cuánta gente se perdió, que es la pregunta que trae al usuario de
 * negocio a esta pantalla.
 */

function porcentaje(valor: number, total: number): number {
  return total > 0 ? Math.round((valor / total) * 1000) / 10 : 0;
}

/** Separador de miles. Sin esto, «1240» y «1.240» conviven en la misma pantalla. */
const cifra = (valor: number): string => valor.toLocaleString('es-CL');

function Tarjeta({
  titulo,
  valor,
  detalle,
  tono,
}: {
  titulo: string;
  valor: string;
  detalle?: string;
  tono?: 'bien' | 'mal' | 'aviso';
}) {
  return (
    <div className={`tarjeta ${tono ?? ''}`}>
      <span className="tarjeta-titulo">{titulo}</span>
      <strong className="tarjeta-valor">{valor}</strong>
      {detalle && <span className="tarjeta-detalle">{detalle}</span>}
    </div>
  );
}

/**
 * Cuántos terminaron por el negocio y cuántos por una falla.
 *
 * Con desglose, «de negocio» suma los que el SDK ya cerró como
 * `BUSINESS_ERROR` y los `ERROR` de clientes que todavía no actualizaron pero
 * cuyas llamadas dicen que fue un rechazo; lo que queda es falla. Sin
 * desglose —ventanas largas, donde el rollup no guarda las llamadas— solo se
 * conoce lo que el SDK cerró como `BUSINESS_ERROR`.
 *
 * Así las cuatro tarjetas suman las ejecuciones.
 */
function repartoDeErrores(datos: EmbudoDatos, causas: Causas | null) {
  if (causas) {
    return {
      negocio: causas.negocio,
      falla: causas.servicio + causas.otro,
      detalle: [
        causas.servicio > 0 ? `${cifra(causas.servicio)} por servicios` : '',
        causas.otro > 0 ? `${cifra(causas.otro)} sin llamada fallida` : '',
      ].filter(Boolean),
    };
  }
  return { negocio: datos.con_negocio, falla: datos.con_error, detalle: [] as string[] };
}

function Paso({ paso, anterior }: { paso: PasoEmbudo; anterior: PasoEmbudo | null }) {
  const perdidos = anterior ? anterior.llegaron - paso.llegaron : 0;
  const conErrores = paso.errores > 0;

  return (
    <>
      {anterior && perdidos > 0 && (
        <div className="caida">
          ↓ se perdieron <strong>{perdidos}</strong> ({paso.caida_pct}%)
        </div>
      )}

      <div className="paso">
        <div className="paso-cabecera">
          <span className="paso-nombre">{paso.paso}</span>
          <span className="paso-metricas">
            p50 {paso.p50_ms} ms · p95 {paso.p95_ms} ms
          </span>
        </div>

        <div className="barra-fondo">
          <div
            className={`barra ${conErrores ? 'con-errores' : ''}`}
            style={{ width: `${Math.max(paso.alcance_pct, 2)}%` }}
          >
            <span className="barra-etiqueta">
              {cifra(paso.llegaron)} · {paso.alcance_pct}%
            </span>
          </div>
        </div>

        {conErrores && (
          <div className="paso-errores">
            {paso.errores} con error ({porcentaje(paso.errores, paso.llegaron)}% de los que llegaron)
          </div>
        )}
      </div>
    </>
  );
}

export function Embudo({ datos, causas }: { datos: EmbudoDatos; causas: Causas | null }) {
  const { ejecuciones, completados, abandonados, pasos } = datos;
  const reparto = repartoDeErrores(datos, causas);

  if (ejecuciones === 0) {
    return (
      <p className="vacio">
        No hay ejecuciones de <code>{datos.proceso}</code> en esta ventana de tiempo.
      </p>
    );
  }

  return (
    <section>
      <div className="tarjetas">
        <Tarjeta titulo="Ejecuciones" valor={cifra(ejecuciones)} />
        <Tarjeta
          titulo="Completadas"
          valor={`${porcentaje(completados, ejecuciones)}%`}
          detalle={`${cifra(completados)} de ${cifra(ejecuciones)}`}
          tono="bien"
        />
        <Tarjeta
          titulo="Abandonadas"
          valor={`${porcentaje(abandonados, ejecuciones)}%`}
          detalle={`${cifra(abandonados)} sin completar`}
          tono="aviso"
        />
        <Tarjeta
          titulo="Con error"
          valor={`${porcentaje(reparto.falla, ejecuciones)}%`}
          detalle={[`${cifra(reparto.falla)} fallidas`, ...reparto.detalle].join(' · ')}
          tono="mal"
        />
        {reparto.negocio > 0 && (
          // Sin color: es el sistema respondiendo que no, no una alarma.
          <Tarjeta
            titulo="De negocio"
            valor={`${porcentaje(reparto.negocio, ejecuciones)}%`}
            detalle={`${cifra(reparto.negocio)} rechazos`}
          />
        )}
      </div>

      <div className="embudo">
        {pasos.map((paso, i) => (
          <Paso key={paso.paso} paso={paso} anterior={i > 0 ? (pasos[i - 1] ?? null) : null} />
        ))}
      </div>
    </section>
  );
}
